import {
  SetStateAction,
  createContext,
  useEffect,
  useState,
  Dispatch,
} from 'react';
import * as Yup from 'yup';
import { LoadingSpinner } from '../elements/shared-components/src/index';
import { merge } from 'ts-deepmerge';
import BaseConfig from '../configs/src/base';
import { getAdditionalConfig, FieldConfig, RequiredType } from './helpers';
import React from 'react';
import { RCFN, RCOParamMap } from '../configs/src/utils/render-control';
import { subscribeDocument } from '../utils/api/api';
import {
  DTTenant_Public,
  TenantInfo,
  ProcessedAlgoliaConfig,
  FBD_AlgoliaConfig,
} from '../data/types/src/index';
import AdditionalBaseConfig, {
  AdditionalFields,
  DisplayType,
  EditableTypes,
} from './additional-config/base';
import { processAlgoliaConfig } from './helpers';

export type MergedConfigType = typeof BaseConfig &
  Partial<typeof AdditionalBaseConfig>;

export interface ConfigInterface {
  tenantInfo: DTTenant_Public['info'] | undefined;
  activeTenantLink: string | null;
  config: MergedConfigType;
  setConfig: Dispatch<SetStateAction<MergedConfigType>>;
  RenderControl: <K extends keyof RCOParamMap>(
    option: K,
    params: RCOParamMap[K]
  ) => boolean;
  algoliaConfig: ProcessedAlgoliaConfig | null;
  algoliaConfigLoading: boolean;
}

const AppContext = createContext<ConfigInterface>({
  tenantInfo: undefined,
  activeTenantLink: null,
  config: BaseConfig as MergedConfigType,
  setConfig: () => {},
  RenderControl: () => false,
  algoliaConfig: null,
  algoliaConfigLoading: true,
});

type ConfigProviderWrapperProps = {
  children: React.ReactNode;
};

const AppContextProviderWrapper = ({
  children,
}: ConfigProviderWrapperProps) => {
  const [config, setConfig] = useState<MergedConfigType>(
    BaseConfig as MergedConfigType
  );
  const [isLoading, setIsLoading] = useState(true);
  // const isEmulator = import.meta.env.VITE_FIREBASE_MODE === 'EMULATOR';
  // const localConfig = localStorage.getItem('CONFIG');
  const [activeTenantLink, setActiveTenantLink] = useState(
    localStorage.getItem('activeTenant')
  );
  const [tenantInfo, setTenantInfo] = useState<TenantInfo>();

  const [algoliaConfig, setAlgoliaConfig] = useState<any | null>(null);
  const [algoliaConfigLoading, setAlgoliaConfigLoading] = useState(true);

  const loadAlgoliaConfig = async (tenantLink: string): Promise<void> => {
    const startTime = performance.now();
    setAlgoliaConfigLoading(true);

    try {
      // console.log(`AppContext: Loading Algolia config for ${tenantLink}...`);

      // Direct FBD query for blazing fast performance
      const queryResults = await FBD_AlgoliaConfig.query()
        .where('tenantLink', '==', tenantLink)
        .where('isActive', '==', true)
        // .orderBy('priority', 'desc') // Higher priority first
        // .orderBy('tcreate', 'desc')   // Newer configs first as tiebreaker
        .limit(1)
        .getContents();

      let processedConfig: ProcessedAlgoliaConfig;

      if (queryResults && queryResults.length > 0) {
        // Process the database config
        const rawConfig = queryResults[0];

        try {
          processedConfig = processAlgoliaConfig(rawConfig);

          // Set the processed config to state
          setAlgoliaConfig(processedConfig);

          // const loadTime = performance.now() - startTime;
          // console.log(
          //   `✅ AppContext: Algolia config loaded in ${loadTime.toFixed(1)}ms`
          // );
        } catch (processingError) {
          console.error(
            'AppContext: Error processing config:',
            processingError
          );
          throw processingError; // Re-throw to be caught by outer catch
        }
      } else {
        // Create fallback config if no database config found
        console.log(`No Algolia config found in database for ${tenantLink}`);
      }
    } catch (error) {
      // dont show any error or warning messages
      console.log('AppContext: Failed to load Algolia config:', error);
      // dont throw any error
    } finally {
      setAlgoliaConfigLoading(false);
    }
  };

  useEffect(() => {
    async function updateActiveTenantLink() {
      const activeTenant = localStorage.getItem('activeTenant');
      if (activeTenant) {
        setActiveTenantLink(activeTenant);
      }
    }

    window.addEventListener('activeTenantChange', updateActiveTenantLink);
    void updateActiveTenantLink();
    return () => {
      window.removeEventListener('activeTenantChange', updateActiveTenantLink);
    };
  }, []);

  // console.debug('activeTenantLink', activeTenantLink); // @TEST_REMOVE_LATER

  useEffect(() => {
    async function getTenantConfig(tenantLink: string) {
      const tenant = tenantLink.toLowerCase();
      try {
        return await import(`../configs/src/${tenant}.ts`);
      } catch (e) {
        console.warn(`File ${`../configs/src/${tenant}`} not found`);
        return BaseConfig;
      }
    }

    if (activeTenantLink) {
      // console.debug('activeTenantLink', activeTenantLink); // @TEST_REMOVE_LATER

      void loadAlgoliaConfig(activeTenantLink);

      void getTenantConfig(activeTenantLink).then((config) => {
        getAdditionalConfig(activeTenantLink).then((additionalConfig) => {
          const finalConfig = merge(
            BaseConfig,
            config.default as typeof BaseConfig,
            additionalConfig.default as typeof AdditionalBaseConfig
          );

          validateTenantConfig(finalConfig);
          // console.debug('finalConfig', finalConfig);
          setConfig(finalConfig as unknown as MergedConfigType);
          setIsLoading(false);
        });
      });

      const unSub = subscribeDocument<DTTenant_Public>(
        `tenant_public/${activeTenantLink}`,
        (doc) => {
          const info = doc.data()?.info;
          if (info?.brandDetails && typeof info.brandDetails === 'string') {
            try {
              const parsedBrandDetails = JSON.parse(info.brandDetails);
              setTenantInfo({
                ...info,
                brandDetails: parsedBrandDetails
              });
            } catch (error) {
              console.error('Failed to parse brandDetails:', error);
              setTenantInfo(info);
            }
          } else {
            setTenantInfo(info);
          }
          
        }
      );

      return () => {
        unSub();
      };
    }
  }, [activeTenantLink]);

  if (!activeTenantLink) return <LoadingSpinner size="md" />;

  const RenderControl: ConfigInterface['RenderControl'] = (option, params) => {
    return RCFN[option](config, params);
  };

  /* -------------------------------------------------------------------------- */
  /*                               Provider return                              */
  /* -------------------------------------------------------------------------- */
  if (!RenderControl || !setConfig || isLoading)
    return <LoadingSpinner size="sm" />;

  return (
    <AppContext.Provider
      value={{
        tenantInfo,
        activeTenantLink,
        config,
        setConfig,
        RenderControl,
        algoliaConfig,
        algoliaConfigLoading,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

function getTenantConfigValidationSchema() {
  // Schema for additional field validation
  const additionalFieldSchema = Yup.object({
    type: Yup.mixed<
      | 'string'
      | 'radio'
      | 'single-select'
      | 'boolean'
      | 'auto-complete-suggestion-text'
    >()
      .oneOf([
        'string',
        'radio',
        'single-select',
        'boolean',
        'auto-complete-suggestion-text',
      ])
      .required('Type is required'),
    title: Yup.string().required('Title is required'),
    display: Yup.array().of(Yup.string().oneOf(Object.values(DisplayType))),
    initialValue: Yup.mixed().test(
      'type-match',
      "Initial value type must match 'type'",
      function (value) {
        const type = this.parent.type;
        if (
          type === 'string' &&
          typeof value !== 'string' &&
          value !== undefined
        )
          return false;
        if (
          type === 'integer' &&
          typeof value !== 'number' &&
          value !== undefined
        )
          return false;
        return true;
      }
    ),
    validation: Yup.object({
      required: Yup.mixed<
        RequiredType.REQUIRED_FOR_ALL | RequiredType.REQUIRED_FOR_CONSUMER
      >().oneOf([
        RequiredType.REQUIRED_FOR_ALL,
        RequiredType.REQUIRED_FOR_CONSUMER,
      ]),
      minValue: Yup.number()
        .when('required', {
          is: true,
          then: (schema) => schema.min(0, 'minValue cannot be negative'),
        })
        .nullable(),
    }).nullable(),
    decoration: Yup.object({
      placeholder: Yup.string(),
    }).nullable(),
  });

  const configSchema = Yup.object().shape({
    HOLDINGS: Yup.object()
      .shape({
        NEW_REGISTRATION_FLOW: Yup.object()
          .shape({
            ADDITIONAL_FIELDS: Yup.object()
              .test(
                'validate-fields',
                'Invalid additional field configuration',
                function (fields) {
                  if (!fields) return true;
                  return Object.values(fields).every((field) =>
                    additionalFieldSchema.isValidSync(field)
                  );
                }
              )
              .nullable(),
          })
          .required(),
      })
      .required(),
  });

  return configSchema;
}

function createDynamicYupSchema(
  fields: { [key: string]: FieldConfig },
  requiredType: RequiredType,
  editable?: EditableTypes,
  customSchemaMapper?: { [key: string]: (field: FieldConfig) => Yup.AnySchema }
) {
  const schema: Record<string, any> = {};

  // Get fields to validate based on editable parameter
  const fieldsToValidate = editable
    ? Object.entries(fields).filter(([_, field]) =>
        field.editable?.includes(editable)
      )
    : Object.entries(fields);

  fieldsToValidate.forEach(([key, field]) => {
    if (!field.validation?.required && !field.validation?.regex) {
      return; // Skip fields without required validation or regex validation
    }

    const isRequired = field.validation.required === requiredType;
    // console.log(
    //   'Field Key With Required Type',
    //   key,
    //   field.validation.required,
    //   requiredType
    // );

    // Check if there's a custom schema mapper for this field type
    if (customSchemaMapper && customSchemaMapper[field.type]) {
      schema[key] = customSchemaMapper[field.type](field);
      return;
    }

    let baseValidation;
    switch (field.type) {
      case 'string':
      case 'radio':
      case 'single-select':
        baseValidation = Yup.string().trim(); // trim only needed for string types

        // Apply regex validation if regex pattern is found
        if (field.validation?.regex) {
          baseValidation = baseValidation.matches(
            new RegExp(field.validation.regex),
            `${field.label || field.title} is invalid`
          );
        }

        if (
          field.validation.required === RequiredType.REQUIRED_FOR_ALL ||
          isRequired
        ) {
          schema[key] = baseValidation.required(
            `${field.label || field.title} is required`
          );
        } else {
          schema[key] = baseValidation;
        }
        break;
      case 'boolean':
        schema[key] = Yup.boolean().optional();
        break;
      case 'multi-select':
        baseValidation = Yup.array();
        if (
          field.validation.required === RequiredType.REQUIRED_FOR_ALL ||
          isRequired
        ) {
          baseValidation = baseValidation.min(
            1,
            `${field.title} requires at least one selection`
          );
        }
        schema[key] = baseValidation;
        break;
      case 'auto-complete-suggestion-text':
        schema[key] = Yup.string().required(`${field.title} is required`);
        break;
      default:
        schema[key] = Yup.mixed();
    }
  });

  return Yup.object().shape(schema);
}

// Get the fields to display in the form
const getEditableFields = (
  fields: AdditionalFields,
  editableType: EditableTypes
): AdditionalFields => {
  return Object.entries(fields)
    .filter(([_, field]) =>
      field.editable?.includes(EditableTypes.EDITABLE_FOR_CONSUMER)
    )
    .reduce((acc, [key, field]) => {
      acc[key] = field;
      return acc;
    }, {} as AdditionalFields);
};

const validateTenantConfig = (config: any) => {
  const configSchema = getTenantConfigValidationSchema();

  try {
    configSchema.validateSync(config, { abortEarly: false });
  } catch (err: any) {
    console.error('Invalid configuration:', err.errors);
    throw new Error('Invalid configuration');
  }
};

export {
  AppContext,
  AppContextProviderWrapper,
  createDynamicYupSchema,
  getEditableFields,
};
